"use client";

import { useId, useRef, useState } from "react";
import { track } from "@vercel/analytics";

import {
  TurnstileCheck,
  TurnstileScript,
  useTurnstile,
} from "@/components/ownly/TurnstileWidget";
import { isAnalyticsQaUrl } from "@/lib/analytics-filter";
import { campaignFields } from "@/lib/campaign-source";
import { isValidEmail } from "@/lib/forms/validation";

import {
  BOOKING_GATE_QUESTIONS,
  bookingPhone,
  budgetRangeFor,
  EMAIL_OPT_IN_LABEL,
  HEARD_ABOUT_PROMPT,
  HEARD_ABOUT_SOURCES,
  heardAboutAnswer,
  TIMELINE_OPTIONS,
  type PreCallAnswers,
  type PreCallChoiceKey,
  type TimelineId,
  WANTED_OUTCOME,
  WHATSAPP_OPT_IN_LABEL,
} from "@/lib/precall";

/* The two choice questions, then "How did you hear about us?", then name and email.
 *
 * NAME AND EMAIL BEFORE THE CALENDAR (Tom, 2026-09-23). Until now Calendly collected them, so
 * somebody who answered every question and then closed the tab before picking a time left us
 * nothing at all. They are asked last, once the person has already invested in the questions,
 * and filed in Lead Engine the moment the calendar opens. Calendly is then prefilled with both,
 * so nobody types their details twice. The prefill goes into the calendar frame's own address,
 * never this page's, so it cannot reach our analytics. */
const HEARD_ABOUT_STEP = BOOKING_GATE_QUESTIONS.length;
const DETAILS_STEP = HEARD_ABOUT_STEP + 1;
const TOTAL_STEPS = DETAILS_STEP + 1;

/* However slow the filing is, the calendar opens after this long. A brief that failed to file
   costs us preparation; a calendar that never opened costs us the customer. */
const FILE_TIMEOUT_MS = 4000;

function isAutomatedOrQaVisit(): boolean {
  if (navigator.webdriver === true) return true;
  if (isAnalyticsQaUrl(window.location.href)) return true;
  try {
    return window.sessionStorage.getItem("ownly-analytics-qa") === "1";
  } catch {
    return false;
  }
}

const DETAIL_INPUT_CLASS =
  "mt-2 h-[3.25rem] w-full rounded-xl border border-white/[0.18] bg-white/[0.03] px-4 text-[16px] text-white placeholder:text-white/35 transition-colors hover:border-white/[0.32] focus-visible:border-ownly-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent";

function OptionRow({ selected, label, multi, onSelect }: {
  selected: boolean;
  label: string;
  multi: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`flex min-h-[52px] w-full items-center gap-3.5 rounded-xl border px-4 py-3 text-left text-[16px] leading-[22px] transition-[border-color,background-color,box-shadow,transform] duration-200 active:scale-[0.99] ${
        selected
          ? "border-ownly-accent/60 bg-ownly-accent/15 text-white shadow-[0_0_0_1px_rgba(43,127,255,0.25),0_10px_28px_-18px_rgba(43,127,255,0.55)]"
          : "border-white/[0.14] bg-white/[0.02] text-white/85 hover:border-white/[0.32] hover:bg-white/[0.05]"
      }`}
    >
      <span
        className={`inline-flex size-[18px] shrink-0 items-center justify-center border text-sm transition-colors duration-200 ${
          multi ? "rounded-[5px]" : "rounded-full"
        } ${selected ? "border-ownly-accent bg-ownly-accent text-white" : "border-white/35 text-transparent"}`}
        aria-hidden
      >
        {multi ? "✓" : <span className={`size-2 rounded-full ${selected ? "bg-white" : ""}`} />}
      </span>
      {label}
    </button>
  );
}

/**
 * One consent box. Unticked by default, always: a pre-ticked box is not consent under UK GDPR or
 * PECR. A real checkbox (not a styled button) so it is announced and toggled the standard way.
 */
function ConsentBox({ id, checked, onChange, children }: {
  id: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-[3px] size-5 shrink-0 cursor-pointer rounded-[5px] accent-[var(--ownly-accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent"
      />
      <label htmlFor={id} className="cursor-pointer text-copy leading-[22px] text-white/75">
        {children}
      </label>
    </div>
  );
}

function PrimaryButton({ onClick, children, disabled = false }: {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="ownly-btn-primary inline-flex h-12 w-full items-center justify-center rounded-full bg-ownly-accent px-6 text-copy font-semibold text-white shadow-[var(--ownly-cta-shadow)] transition-[box-shadow,transform,background-color] duration-300 ease-out hover:-translate-y-0.5 hover:bg-[#3d8aff] hover:shadow-[var(--ownly-cta-shadow-hover)] active:translate-y-0 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 sm:w-auto sm:min-w-[210px]"
    >
      {children}
    </button>
  );
}

/** Three quick questions and a name and email, then straight to live availability. */
export function PreCallForm({
  onReady,
  ownBooking = false,
}: {
  onReady: (answers: PreCallAnswers) => void;
  /** Ownly's own picker comes next instead of Calendly, which changes two lines of copy. */
  ownBooking?: boolean;
}) {
  const headingId = `${useId()}-booking-question`;
  const sourceId = `${useId()}-heard-about`;
  const detailId = `${useId()}-heard-about-detail`;
  const [step, setStep] = useState(0);
  const [choices, setChoices] = useState<Partial<Record<PreCallChoiceKey, string[]>>>({});
  const [source, setSource] = useState("");
  const [sourceOption, setSourceOption] = useState("");
  const [sourceDetail, setSourceDetail] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [wantedOutcome, setWantedOutcome] = useState("");
  const [timeline, setTimeline] = useState<TimelineId | "">("");
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const startedRef = useRef(false);
  const turnstile = useTurnstile();
  const nameId = `${useId()}-name`;
  const emailId = `${useId()}-email`;
  const phoneId = `${useId()}-phone`;
  const phoneHintId = `${useId()}-phone-hint`;
  const businessId = `${useId()}-business`;
  const outcomeId = `${useId()}-wanted-outcome`;
  const timelineHeadingId = `${useId()}-timeline`;
  const emailOptInId = `${useId()}-email-opt-in`;
  const whatsappOptInId = `${useId()}-whatsapp-opt-in`;
  /* The WhatsApp box only means something once there is a number to message. */
  const hasPhone = phoneNumber.trim().length > 0;

  const onHeardAbout = step === HEARD_ABOUT_STEP;
  const onDetails = step === DETAILS_STEP;
  const question = onHeardAbout || onDetails ? null : BOOKING_GATE_QUESTIONS[step];
  const selected = question ? (choices[question.key] ?? []) : [];
  const channel = HEARD_ABOUT_SOURCES.find((s) => s.label === source);

  function recordStart(stepKey: string) {
    if (startedRef.current) return;
    startedRef.current = true;
    track("booking-qualifier-started", { step: stepKey });
  }

  function choose(option: string) {
    if (!question) return;
    recordStart(question.key);
    setChoices((current) => {
      const previous = current[question.key] ?? [];
      const next = question.multi
        ? previous.includes(option)
          ? previous.filter((value) => value !== option)
          : [...previous, option]
        : [option];
      return { ...current, [question.key]: next };
    });
  }

  function chooseSource(label: string) {
    recordStart("heardAbout");
    setSource(label);
    /* A follow-up picked for one channel means nothing under another. */
    setSourceOption("");
    setSourceDetail("");
  }

  function next() {
    if (!question) return;
    /* Both gate questions need an answer: what they want to talk about, and a budget band. */
    if (!selected.length) return;
    track("booking-qualifier-step", { step: question.key, answered: selected.length > 0 });
    setStep(step + 1);
  }

  function toDetails() {
    if (!channel) return;
    track("booking-qualifier-completed", {
      wants: (choices.wantToTalkAbout ?? []).length ? "given" : "not-given",
      budget: (choices.budget ?? []).length ? "given" : "not-given",
      /* The channel only, never the free text: a name typed in here is not analytics. */
      heardAbout: channel.label,
      heardAboutDetail: sourceOption || "none",
    });
    setStep(DETAILS_STEP);
  }

  /**
   * File what they told us in Lead Engine, then open the calendar whatever happens.
   *
   * Through this site's own /api/intake (rate limit, honeypot, Turnstile, test mode on previews),
   * never straight to the CRM. If the security check has not finished, or the request fails or
   * is slow, the calendar still opens: the booking itself carries every answer to Lead Engine
   * through Calendly, so the only thing lost is the record of somebody who then did not book.
   */
  async function fileAnswers(answers: PreCallAnswers): Promise<"filed" | "skipped" | "failed"> {
    /* The site's own smoke tests walk this form every day against the live site, in a driven
       browser or a tab marked ?ownly_qa. Filing then would put a fake lead in the CRM daily. */
    if (isAutomatedOrQaVisit()) return "skipped";
    if (!turnstile.ready || !turnstile.token) return "skipped";
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), FILE_TIMEOUT_MS);
    try {
      const res = await fetch("/api/intake", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          mode: "precall",
          name: answers.name,
          email: answers.email,
          phone: answers.phone,
          businessName: answers.businessName,
          wantToTalkAbout: answers.wantToTalkAbout,
          budget: answers.budget,
          budgetRange: budgetRangeFor(answers.budget),
          heardAbout: answers.heardAbout,
          wantedOutcome: answers.wantedOutcome,
          timeline: answers.timeline,
          emailOptIn: answers.emailOptIn === true,
          whatsappOptIn: answers.whatsappOptIn === true,
          website: honeypot,
          turnstileToken: turnstile.token,
          ...campaignFields(),
        }),
      });
      return res.ok ? "filed" : "failed";
    } catch {
      return "failed";
    } finally {
      window.clearTimeout(timer);
      turnstile.reset();
    }
  }

  async function openCalendar() {
    if (!channel || opening) return;
    const trimmedName = name.replace(/\s+/g, " ").trim();
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedName) {
      setDetailsError("Please add your name.");
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setDetailsError("Please check your email address. It looks like something is missing.");
      return;
    }
    const phone = bookingPhone(phoneNumber);
    if (!phone.ok) {
      setDetailsError(
        phone.reason === "missing"
          ? "Please add a phone number, in case we need to reach you about the call."
          : "Please check your phone number. It looks like a digit is missing or extra.",
      );
      return;
    }
    setDetailsError(null);
    setOpening(true);

    const answers: PreCallAnswers = {
      name: trimmedName.slice(0, 120),
      businessName: businessName.replace(/\s+/g, " ").trim().slice(0, 200),
      email: trimmedEmail.slice(0, 254),
      phone: phone.value,
      wantToTalkAbout: (choices.wantToTalkAbout ?? []).join(", ") || undefined,
      budget: (choices.budget ?? []).join(", ") || undefined,
      heardAbout: heardAboutAnswer(channel.label, sourceOption, sourceDetail),
      wantedOutcome: wantedOutcome.replace(/\s+/g, " ").trim().slice(0, WANTED_OUTCOME.maxLength) || undefined,
      timeline: timeline || undefined,
      emailOptIn,
      whatsappOptIn: whatsappOptIn && hasPhone,
    };
    const filed = await fileAnswers(answers);
    track("booking-details-given", {
      filed,
      business: answers.businessName ? "given" : "not-given",
      /* Whether, never what: the words they typed are not analytics. */
      outcome: answers.wantedOutcome ? "given" : "not-given",
      timeline: answers.timeline ?? "not-given",
      emailOptIn: answers.emailOptIn ? "yes" : "no",
      whatsappOptIn: answers.whatsappOptIn ? "yes" : "no",
    });
    onReady(answers);
  }

  return (
    <div className="mt-8">
      <div className="max-w-[42rem] scroll-mt-24 rounded-2xl border border-white/[0.12] bg-gradient-to-b from-white/[0.06] to-ownly-surface/90 px-5 py-6 shadow-[var(--ownly-shadow-card)] sm:px-9 sm:py-8">
        <div className="flex min-h-[2.75rem] items-center justify-between gap-4">
          {step > 0 ? (
            <button
              type="button"
              onClick={() => setStep(step - 1)}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full text-copy font-medium text-white/60 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent"
            >
              <span aria-hidden className="text-[17px] leading-none">‹</span>
              Back
            </button>
          ) : (
            <p className="ownly-eyebrow">Before you pick a time</p>
          )}
          <p className="font-mono text-[12px] tracking-[0.14em] text-white/50" aria-live="polite">
            {step + 1} of {TOTAL_STEPS}
          </p>
        </div>

        <div
          className="mt-2 h-1 overflow-hidden rounded-full bg-white/[0.08]"
          role="progressbar"
          aria-label="Booking questions answered"
          aria-valuemin={1}
          aria-valuemax={TOTAL_STEPS}
          aria-valuenow={step + 1}
        >
          <div
            className="ownly-step-progress h-full w-full origin-left rounded-full bg-gradient-to-r from-ownly-accent to-[var(--ownly-violet)]"
            style={{ transform: `scaleX(${(step + 1) / TOTAL_STEPS})` }}
          />
        </div>

        <div key={step} className="ownly-step-in-fwd mt-7 min-h-[280px]">
          {step === 0 ? (
            <div className="mb-7 border-b border-white/[0.1] pb-6">
              <h2 className="ownly-display ownly-section-title max-w-[24ch] text-balance text-white">
                Three quick questions, then pick a time
              </h2>
              <p className="mt-3 max-w-2xl text-pretty text-copy leading-[24px] text-white/68">
                This gives us enough to prepare, so the review starts with your business. It
                takes about a minute.
              </p>
            </div>
          ) : null}

          {question ? (
            <div role="group" aria-labelledby={headingId}>
              <h3
                id={headingId}
                className="ownly-display ownly-card-title text-white"
              >
                {question.prompt}
              </h3>
              {question.hint ? (
                <p className="mt-2 max-w-[46ch] text-copy leading-[21px] text-white/60">{question.hint}</p>
              ) : null}
              <div className="mt-6 grid gap-2.5 sm:grid-cols-2">
                {question.options.map((option) => (
                  <OptionRow
                    key={option}
                    label={option}
                    multi={Boolean(question.multi)}
                    selected={selected.includes(option)}
                    onSelect={() => choose(option)}
                  />
                ))}
              </div>
              {question.key === "wantToTalkAbout" ? (
                <div className="mt-6">
                  <label htmlFor={outcomeId} className="text-copy font-medium leading-[21px] text-white/80">
                    {WANTED_OUTCOME.prompt}{" "}
                    <span className="font-normal text-white/55">(optional)</span>
                  </label>
                  <textarea
                    id={outcomeId}
                    value={wantedOutcome}
                    rows={2}
                    maxLength={WANTED_OUTCOME.maxLength}
                    onChange={(event) => setWantedOutcome(event.target.value)}
                    placeholder={WANTED_OUTCOME.placeholder}
                    className="mt-2 block min-h-[5.25rem] w-full resize-y rounded-xl border border-white/[0.18] bg-white/[0.03] px-4 py-3 text-[16px] leading-[24px] text-white placeholder:text-white/35 transition-colors hover:border-white/[0.32] focus-visible:border-ownly-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent"
                  />
                </div>
              ) : null}
              {question.key === "budget" ? (
                <div role="group" aria-labelledby={timelineHeadingId} className="mt-7">
                  <p id={timelineHeadingId} className="text-copy font-medium leading-[21px] text-white/80">
                    When would you like it?{" "}
                    <span className="font-normal text-white/55">(optional)</span>
                  </p>
                  <div className="mt-3 grid grid-cols-2 gap-2.5">
                    {TIMELINE_OPTIONS.map((option) => (
                      <OptionRow
                        key={option.id}
                        label={option.label}
                        multi={false}
                        selected={timeline === option.id}
                        onSelect={() => setTimeline(timeline === option.id ? "" : option.id)}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="mt-7">
                <PrimaryButton onClick={next} disabled={!selected.length}>
                  Continue
                </PrimaryButton>
              </div>
            </div>
          ) : onHeardAbout ? (
            <div>
              <label
                htmlFor={sourceId}
                className="ownly-display block text-[23px] leading-[29px] text-white sm:text-[27px] sm:leading-[34px]"
              >
                {HEARD_ABOUT_PROMPT}
              </label>
              <div className="relative mt-6">
                <select
                  id={sourceId}
                  value={source}
                  onChange={(event) => chooseSource(event.target.value)}
                  className={`h-[3.25rem] w-full appearance-none rounded-xl border bg-white/[0.03] pl-4 pr-12 text-[16px] transition-[border-color,background-color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent ${
                    source
                      ? "border-ownly-accent/60 text-white"
                      : "border-white/[0.18] text-white/60 hover:border-white/[0.32]"
                  }`}
                >
                  <option value="" disabled className="bg-[#0f1525] text-white/60">
                    Choose one
                  </option>
                  {HEARD_ABOUT_SOURCES.map((s) => (
                    <option key={s.label} value={s.label} className="bg-[#0f1525] text-white">
                      {s.label}
                    </option>
                  ))}
                </select>
                <span
                  aria-hidden
                  className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[16px] text-white/60"
                >
                  ▾
                </span>
              </div>

              {channel?.options ? (
                <div
                  key={channel.label}
                  role="group"
                  aria-label={`Which ${channel.label.toLowerCase().replace(/^an? /, "")}?`}
                  className="ownly-step-in-fwd mt-5"
                >
                  <p className="text-copy leading-[21px] text-white/60">Which one?</p>
                  <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
                    {channel.options.map((option) => (
                      <OptionRow
                        key={option}
                        label={option}
                        multi={false}
                        selected={sourceOption === option}
                        onSelect={() => setSourceOption(sourceOption === option ? "" : option)}
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {channel?.detail ? (
                <div key={`${channel.label}-detail`} className="ownly-step-in-fwd mt-5">
                  <label htmlFor={detailId} className="text-copy leading-[21px] text-white/60">
                    {channel.detail.prompt} <span className="text-white/55">(optional)</span>
                  </label>
                  <input
                    id={detailId}
                    type="text"
                    value={sourceDetail}
                    maxLength={120}
                    onChange={(event) => setSourceDetail(event.target.value)}
                    placeholder={channel.detail.placeholder}
                    className="mt-2 h-[3.25rem] w-full rounded-xl border border-white/[0.18] bg-white/[0.03] px-4 text-[16px] text-white placeholder:text-white/35 transition-colors hover:border-white/[0.32] focus-visible:border-ownly-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ownly-accent"
                  />
                </div>
              ) : null}

              <div className="mt-7">
                <PrimaryButton onClick={toDetails} disabled={!channel}>
                  Continue
                </PrimaryButton>
              </div>
            </div>
          ) : (
            <form
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                void openCalendar();
              }}
            >
              <TurnstileScript />
              <h3 className="ownly-display ownly-card-title text-white">
                Who should we expect?
              </h3>
              <p className="mt-2 max-w-[46ch] text-copy leading-[21px] text-white/60">
                {ownBooking
                  ? "We will put these on the booking for you, so you only type them once."
                  : "We will fill these in on the calendar for you, so you only type them once."}
              </p>
              <div className="mt-6 grid gap-4">
                <div>
                  <label htmlFor={nameId} className="text-copy font-medium leading-[21px] text-white/80">
                    Your name
                  </label>
                  <input
                    id={nameId}
                    type="text"
                    value={name}
                    maxLength={120}
                    autoComplete="name"
                    onChange={(event) => {
                      setName(event.target.value);
                      setDetailsError(null);
                    }}
                    className={DETAIL_INPUT_CLASS}
                  />
                </div>
                <div>
                  <label htmlFor={emailId} className="text-copy font-medium leading-[21px] text-white/80">
                    Email
                  </label>
                  <input
                    id={emailId}
                    type="email"
                    inputMode="email"
                    value={email}
                    maxLength={254}
                    autoComplete="email"
                    onChange={(event) => {
                      setEmail(event.target.value);
                      setDetailsError(null);
                    }}
                    className={DETAIL_INPUT_CLASS}
                  />
                </div>
                <div>
                  <label htmlFor={phoneId} className="text-copy font-medium leading-[21px] text-white/80">
                    Phone number
                  </label>
                  <input
                    id={phoneId}
                    type="tel"
                    inputMode="tel"
                    value={phoneNumber}
                    maxLength={40}
                    autoComplete="tel"
                    aria-describedby={phoneHintId}
                    onChange={(event) => {
                      setPhoneNumber(event.target.value);
                      setDetailsError(null);
                    }}
                    className={DETAIL_INPUT_CLASS}
                  />
                  <p id={phoneHintId} className="mt-1.5 text-copy leading-[20px] text-white/50">
                    Only used if we need to reach you about the call.
                  </p>
                </div>
                <div>
                  <label htmlFor={businessId} className="text-copy font-medium leading-[21px] text-white/80">
                    Business name <span className="font-normal text-white/55">(optional)</span>
                  </label>
                  <input
                    id={businessId}
                    type="text"
                    value={businessName}
                    maxLength={200}
                    autoComplete="organization"
                    onChange={(event) => setBusinessName(event.target.value)}
                    className={DETAIL_INPUT_CLASS}
                  />
                </div>
                {/* TWO SEPARATE CONSENTS, BOTH UNTICKED (Tom, 2026-10-11). Separate because they are
                    separate permissions: someone happy to be emailed may not want WhatsApp, and
                    the other way round. Neither is needed to book. */}
                <div className="grid gap-3 border-t border-white/[0.1] pt-4">
                  <ConsentBox id={emailOptInId} checked={emailOptIn} onChange={setEmailOptIn}>
                    {EMAIL_OPT_IN_LABEL}
                  </ConsentBox>
                  {hasPhone ? (
                    <ConsentBox id={whatsappOptInId} checked={whatsappOptIn} onChange={setWhatsappOptIn}>
                      {WHATSAPP_OPT_IN_LABEL}
                    </ConsentBox>
                  ) : null}
                </div>
                {/* Honeypot. Off screen, out of the tab order, and never filled by a person. */}
                <div aria-hidden className="absolute left-[-9999px] top-0 h-0 w-0 overflow-hidden">
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(event) => setHoneypot(event.target.value)}
                  />
                </div>
              </div>

              {detailsError ? (
                <p
                  role="alert"
                  className="mt-4 rounded-xl border border-[#E05A45]/35 bg-[#E05A45]/[0.1] px-4 py-3 text-copy text-[#F2AA9D]"
                >
                  {detailsError}
                </p>
              ) : null}

              <div className="mt-5">
                <TurnstileCheck turnstile={turnstile} />
              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={opening}
                  className="ownly-btn-primary inline-flex h-12 w-full items-center justify-center rounded-full bg-ownly-accent px-6 text-copy font-semibold text-white shadow-[var(--ownly-cta-shadow)] transition-[box-shadow,transform,background-color] duration-300 ease-out hover:-translate-y-0.5 hover:bg-[#3d8aff] hover:shadow-[var(--ownly-cta-shadow-hover)] active:translate-y-0 active:scale-[0.98] disabled:cursor-wait disabled:opacity-60 sm:w-auto sm:min-w-[210px]"
                >
                  {opening ? (ownBooking ? "Finding free times…" : "Opening the calendar…") : "Show available times"}
                </button>
              </div>
              <p className="mt-4 max-w-[52ch] text-sm leading-[21px] text-white/55">
                We use these to prepare for the review and to get in touch about this booking.
                Anything more, only if you ticked a box above.{" "}
                {ownBooking
                  ? "Pressing the button shows the times we have free."
                  : "Pressing the button loads our Calendly booking calendar, which sets its own cookies."}{" "}
                See our{" "}
                <a href="/privacy" className="text-white/70 underline underline-offset-2">
                  privacy policy
                </a>
                .
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
